import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { CAROUSEL_MOBILE_WINDOW_SIZE } from './cost-to-build-client-ui-carousel.constants';
import { areCarouselStatesEqual, CAROUSEL_INITIAL_STATE } from './cost-to-build-client-ui-carousel.state';
import type { CarouselState } from './cost-to-build-client-ui-carousel.types';

export const CarouselStore = signalStore(
    withState(CAROUSEL_INITIAL_STATE),
    withComputed((store) => {
        const hasLeftPeek = computed(() => store.windowOffset() > 0);
        const hasRightPeek = computed(() => store.windowOffset() + store.windowSize() < store.thumbnails().length);
        const hasBothPeeks = computed(() => hasLeftPeek() && hasRightPeek());

        // A peek on either side is included whenever a thumbnail exists there, regardless
        // of the window size: this applies just as much to a single-item window (mobile)
        // as to a wider one (desktop) — whenever both a previous and a next item exist,
        // both are shown as peeks at once (see `.ctb-carousel__items--has-both-peeks`,
        // which centers the row so the window items stay fully visible either way).
        const visibleThumbnails = computed(() => {
            const thumbnails = store.thumbnails();
            const offset = store.windowOffset();
            const windowSize = store.windowSize();
            const start = hasLeftPeek() ? offset - 1 : offset;
            const end = hasRightPeek() ? offset + windowSize + 1 : offset + windowSize;

            return thumbnails.slice(start, end);
        });

        return {
            activeDotIndex: computed(() => {
                const thumbnails = store.thumbnails();
                const selectedId = store.selectedId();

                return thumbnails.findIndex((t) => t.id === selectedId);
            }),
            hasBothPeeks,
            hasLeftPeek,
            hasRightPeek,
            state: computed<CarouselState>(() => ({
                ariaLabel: store.ariaLabel(),
                id: store.id(),
                label: store.label(),
                selectedId: store.selectedId(),
                thumbnails: store.thumbnails(),
                windowOffset: store.windowOffset(),
                windowSize: store.windowSize(),
            })),
            visibleThumbnails,
        };
    }),
    withMethods((store) => {
        const setState = (patch: Partial<CarouselState>) => {
            const nextState: CarouselState = {
                ...store.state(),
                ...patch,
            };

            if (areCarouselStatesEqual(store.state(), nextState)) {
                return;
            }

            patchState(store, patch);
        };

        return {
            jumpToDot(index: number) {
                const thumbnails = store.thumbnails();
                const count = thumbnails.length;

                if (index < 0 || index >= count) {
                    return;
                }

                const windowSize = store.windowSize();
                const thumbnail = thumbnails[index];
                const rawOffset = index - Math.floor(windowSize / 2);
                const maxOffset = Math.max(0, count - windowSize);
                const windowOffset = Math.max(0, Math.min(rawOffset, maxOffset));

                setState({ selectedId: thumbnail.id, windowOffset });
            },
            selectThumbnail(id: string) {
                const thumbnails = store.thumbnails();
                const thumbIndex = thumbnails.findIndex((t) => t.id === id);

                if (thumbIndex < 0) {
                    return;
                }

                const offset = store.windowOffset();
                const windowSize = store.windowSize();
                const count = thumbnails.length;
                const rightEdge = offset + windowSize - 1;
                const rightPeekIndex = offset + windowSize;
                const maxOffset = Math.max(0, count - windowSize);
                let nextOffset = offset;

                if (thumbIndex === offset - 1 && offset > 0) {
                    // Clicking the left-peek item scrolls the window left by one.
                    nextOffset = offset - 1;
                } else if (thumbIndex === offset && offset > 0 && windowSize > CAROUSEL_MOBILE_WINDOW_SIZE) {
                    // Leftmost of several window items -> nudge the window left too. Only
                    // meaningful when the window has more than one item; for a single-item
                    // window this index is the current item itself, which should be a no-op.
                    nextOffset = offset - 1;
                } else if (thumbIndex === rightPeekIndex && rightPeekIndex < count) {
                    // Clicking the right-peek item brings it into view, centering it when
                    // possible. Works at any offset, since a peek can appear on either side
                    // as soon as a thumbnail exists there.
                    const centeredOffset = thumbIndex - Math.floor(windowSize / 2);
                    nextOffset = Math.max(0, Math.min(centeredOffset, maxOffset));
                } else if (thumbIndex === rightEdge && offset + windowSize < count && windowSize > CAROUSEL_MOBILE_WINDOW_SIZE) {
                    // Rightmost of several window items -> nudge the window right too.
                    nextOffset = offset + 1;
                }

                setState({ selectedId: id, windowOffset: nextOffset });
            },
            setState,
        };
    })
);
